import React, { useState, useRef } from 'react';
import {
  Popover,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import DownloadIcon from '@mui/icons-material/Download';
import { getFileResourcePreview, getFileDownloadUrl } from '../data';
import { useLocation } from 'react-router-dom';
import { sendFileActivity } from '../../auth/activity';

const ResourcePreviewPopover = ({ file, triggerLabel = 'Preview', icon, driveConfig = undefined }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [getUrl, setGetUrl] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const buttonRef = useRef(null);
  const location = useLocation()

  const resourceId = file?.driveItem?.id

  const handleClick = async (event) => {
    sendFileActivity(file, location, 'Preview')
    const driveId = driveConfig ? driveConfig.driveId : undefined
    
    const previewData = await getFileResourcePreview(resourceId, undefined, driveId)

    const { getUrl: _getUrl } = previewData
    setGetUrl(_getUrl ?? null)

    setAnchorEl(event.target);
  };

  const handleClose = () => {
    setGetUrl(null)
    setIsFullscreen(false)
    setAnchorEl(null);
  }

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  }

  const handleDownload = async () => {
    sendFileActivity(file, location, 'Download')
    const driveId = driveConfig ? driveConfig.driveId : undefined
    const downloadUrl = await getFileDownloadUrl(resourceId, undefined, driveId)
    const link = document.createElement('a');
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  const open = Boolean(anchorEl);
  const id = open ? 'rsrc-prvw-popover' : undefined;

  if (!resourceId) return null;

  return (
    <>
      {icon && <span 
        ref={buttonRef}
        style={{cursor: 'pointer'}}
        onClick={handleClick}
        title={triggerLabel}>{icon}
      </span>}

      {!icon && 
      <Tooltip arrow placement='left' title={triggerLabel}>
        <IconButton
          ref={buttonRef}
          onClick={handleClick}
        >
          <PreviewIcon />
        </IconButton>
      </Tooltip>}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        
        anchorOrigin={
          isFullscreen
            ? { vertical: 'top', horizontal: 'left' } // For fullscreen, anchor from top-left
            : { vertical: 'bottom', horizontal: 'center' }
        }
        transformOrigin={
          isFullscreen
            ? { vertical: 'top', horizontal: 'left' } // For fullscreen, transform from top-left
            : { vertical: 'top', horizontal: 'center' }
        }
        slotProps={{
          paper: {
            sx: {
              // Conditionally apply styles based on isFullscreen state
              width: isFullscreen ? '100vw' : '80vw',
              height: isFullscreen ? 'calc(100vh - 16px)' : '80vh',
              maxWidth: isFullscreen ? '100vw' : 'none', // Remove max-width in fullscreen
              maxHeight: isFullscreen ? '100vh' : 'none', // Remove max-height in fullscreen
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
              // Position fixed for fullscreen to cover the entire viewport
              ...(isFullscreen && {
                position: 'fixed',
                top: '0 !important',
                left: '0 !important',
                borderRadius: 0, // No border-radius in fullscreen
              }),
            },
          },
        }}
      >
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <IconButton onClick={handleDownload} aria-label="download media">
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={handleToggleFullscreen} aria-label="toggle fullscreen">
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
          <IconButton onClick={handleClose} >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <iframe
            src={getUrl}
            title="File Preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      </Popover>
    </>
  );
};

export default ResourcePreviewPopover;
